import { LocalesValue } from "../config";
import { ContentValue, Localized } from "@robe/core-types";

export type ResolvedLocalizedValue<T> = {
  value: T;
  locale: string;
} | null;

/**
 * Resolves value from localized resource.
 * Uses fallback content locale from locales config if current locale is not present in resource
 *
 * @param currentLocale Current page locale
 * @param locales Available locales from config
 */
export const resolveLocalizedValue =
  (currentLocale: string, locales: Record<string, LocalesValue>) =>
  <T extends ContentValue>(resource: Localized<T>, fallbackToAnotherLocale?: boolean): ResolvedLocalizedValue<T> => {
    if (typeof resource === "object" && "value" in resource && typeof resource.value !== "object") {
      return { value: resource.value as unknown as T, locale: "en" };
    }

    const resourceLocales = Object.keys(resource);
    let resolvedCurrentLocale = currentLocale;

    if (resourceLocales.length === 0) {
      return null;
    }

    /**
     * If current locale is not in resource locales see if current locale is one of fallback content values for resource locales and switch to fallback content locale.
     * Used only in admin where current locale is always the same.
     */
    if (process.env.NEXT_PUBLIC_APP_ID === "admin" && !resourceLocales.includes(currentLocale)) {
      const s = resourceLocales.find((rl) => Object.keys(locales).includes(rl) && locales[rl]?.fallbackContentLocale === currentLocale);

      if (s) {
        resolvedCurrentLocale = s;
      }
    }

    // If current locale is in resource return corresponding value
    if (resourceLocales.find((key) => key === resolvedCurrentLocale)) {
      return { value: resource[resolvedCurrentLocale], locale: resolvedCurrentLocale };
    }

    const fallbackLocale = locales[resolvedCurrentLocale]?.fallbackContentLocale;

    // If fallback locale for current locale is in resource return corresponding value
    if (fallbackLocale && resourceLocales.find((key) => key === fallbackLocale)) {
      return { value: resource[fallbackLocale], locale: fallbackLocale };
    }

    // Last resort
    if (fallbackToAnotherLocale) {
      let yoloFallbackLocale = resourceLocales[0];

      if (resourceLocales.includes("en")) {
        yoloFallbackLocale = "en";
      }

      return { value: resource[yoloFallbackLocale], locale: yoloFallbackLocale };
    }

    return null;
  };
